import { Helmet } from 'react-helmet'
import { CircularProgress, Box, Button, Container, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { resolveReferral } from '../../services/referralsService'
import { ROUTES } from '../../helpers/routes-helper'
import { ONBOARD_PATH_PARAMS } from '../../helpers/onboard-helper'
import { errorLog } from '../../utils/log-helper'
import { logEvent } from '../../analytics/analytics'

const PENDING_REFERRAL_CODE_KEY = 'pendingReferralCode'
// Persist the referral context so BaseLayout can route the accept correctly (a RESTAURANT_SIGNUP
// invite must land the invitee in restaurant onboarding, not the eater home).
const PENDING_REFERRAL_CONTEXT_KEY = 'pendingReferralContext'
const RESTAURANT_SIGNUP_CONTEXT = 'RESTAURANT_SIGNUP'

const ReferralLandingPage: React.FC = () => {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, loginWithRedirect } = useAuth0()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [context, setContext] = useState<string>('')

  useEffect(() => {
    const loadReferral = async (): Promise<void> => {
      if (!code) {
        setError('Invalid referral code')
        setLoading(false)
        return
      }

      try {
        // Resolve the referral code (public endpoint, no auth required)
        const resolved = await resolveReferral(code)
        setContext(resolved.context ?? '')
        setLoading(false)
        // Funnel: an invitee opened a valid invite link.
        logEvent('referral_landing_viewed', {
          context: resolved.context ?? '',
          code_prefix: code.substring(0, 8),
          authenticated: isAuthenticated,
        })
      } catch (err: any) {
        errorLog('Failed to resolve referral code', err)
        setError(err.response?.data?.message || 'Invalid or expired referral code')
        setLoading(false)
      }
    }

    void loadReferral()
  }, [code])

  const handleContinue = async (): Promise<void> => {
    if (!code) return

    // Store code (+ context) in localStorage for claim/routing after login.
    localStorage.setItem(PENDING_REFERRAL_CODE_KEY, code)
    localStorage.setItem(PENDING_REFERRAL_CONTEXT_KEY, context)

    // Funnel: the invitee accepted the invite (claim intent). The backend confirmation +
    // attribution fires later in BaseLayout (referral_confirmed).
    logEvent('referral_claimed', {
      context,
      authenticated: isAuthenticated,
      screen_name: 'referral_landing',
    })

    if (isAuthenticated) {
      // Already logged in → auth is settled, so navigating straight to the protected destination is
      // safe. A restaurant invite goes to the role picker (→ restaurant onboarding); an eater invite
      // lands on the eater home. The referral claim happens in the BaseLayout effect.
      const destination =
        context === RESTAURANT_SIGNUP_CONTEXT
          ? `${ROUTES.ONBOARD}/${ONBOARD_PATH_PARAMS.CHOOSE_ROLE}`
          : ROUTES.HOME
      navigate(destination)
    } else {
      // Not logged in → return to a PUBLIC route (home) after Auth0, NEVER directly to a protected
      // onboarding route. Auth0's default onRedirectCallback replaceState()s the URL to returnTo;
      // if that is a guarded route (AuthGuard + withAuthenticationRequired) it is evaluated before
      // isAuthenticated has propagated, AuthGuard bounces to /unauthorized, the Error page auto-fires
      // a fresh login, and the callback ↔ /unauthorized loop begins. Landing on home (public) lets
      // BaseLayout do the restaurant-referral → role-picker routing AFTER auth is confirmed (it reads
      // the pending code + RESTAURANT_SIGNUP context from localStorage), matching every other
      // post-auth resume flow (menu-scan / save / group-session).
      await loginWithRedirect({
        authorizationParams: { screen_hint: 'signup' },
        appState: {
          returnTo: ROUTES.HOME,
        },
      })
    }
  }

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Invalid Invitation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => navigate(ROUTES.HOME)}>
          Go to Home
        </Button>
      </Container>
    )
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Helmet><title>Resactly | You have Been Invited</title></Helmet>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          You&apos;re invited to Resactly
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Create an account or log in to accept your invite.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            void handleContinue()
          }}
          sx={{ minWidth: 200 }}
        >
          Continue
        </Button>
      </Box>
    </Container>
  )
}

export default ReferralLandingPage

// Export the localStorage key for use in BaseLayout
export { PENDING_REFERRAL_CODE_KEY }
